import { hashString, seededPick, seededShuffle } from './rng.js';

const PERFIS_VISUAIS = ['deficiencia_visual', 'baixa_visao', 'cegueira'];
const PERFIS_MOTORES = ['paralisia_cerebral', 'deficiencia_fisica', 'mobilidade_reduzida'];

function normalizeNeed(value = '') {
  return String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

function escolherPerfilPrincipal(necessidades = []) {
  const normalizadas = (Array.isArray(necessidades) ? necessidades : [necessidades]).map(normalizeNeed);
  return normalizadas.find((item) => PERFIS_VISUAIS.includes(item)) || normalizadas.find((item) => PERFIS_MOTORES.includes(item)) || normalizadas.find(Boolean) || 'geral';
}

function produtoCartesiano(opcoes) {
  const entries = Object.entries(opcoes).filter(([, values]) => Array.isArray(values) && values.length);
  return entries.reduce((acc, [key, values]) => acc.flatMap((combo) => values.map((value) => ({ ...combo, [key]: value }))), [{}]);
}

function comboHash(combo) { return String(hashString(JSON.stringify(combo))); }

function filtrarPorPerfil(values = [], perfil = 'geral') {
  if (!Array.isArray(values) || !values.length) return [''];
  if (PERFIS_VISUAIS.includes(perfil)) {
    const acessiveis = values.filter((value) => !/puramente visual|somente visual/i.test(String(value)));
    const preferidas = acessiveis.filter((value) => /tátil|relevo|objeto|auditivo|multissensorial/i.test(String(value)));
    return preferidas.length ? preferidas : acessiveis.length ? acessiveis : values;
  }
  if (PERFIS_MOTORES.includes(perfil)) {
    const filtradas = values.filter((value) => !/corrida|salto alto|pregador firme/i.test(String(value)));
    return filtradas.length ? filtradas : values;
  }
  return values;
}

function priorizarInteresses(contextos = [], interesses = [], seed = '') {
  const lista = Array.isArray(contextos) && contextos.length ? contextos : ['rotina escolar'];
  const interessesNorm = (interesses || []).map(normalizeNeed);
  const matches = lista.filter((ctx) => interessesNorm.some((interesse) => normalizeNeed(ctx).includes(interesse) || interesse.includes(normalizeNeed(ctx))));
  const restante = lista.filter((ctx) => !matches.includes(ctx));
  return [...seededShuffle(matches, `${seed}:interesses`), ...seededShuffle(restante, `${seed}:contextos`)];
}

function apoioPorNivel(apoios = [], nivel = 1) {
  if (!apoios.length) return ['visual'];
  if (nivel <= 1) return apoios.slice(0, Math.max(1, Math.ceil(apoios.length / 2)));
  if (nivel >= 3) return apoios.slice(Math.floor(apoios.length / 2));
  return apoios;
}

function resultadoParaNivel(base, resultadoRecente) {
  if (resultadoRecente === 'facil' || resultadoRecente === 'conseguiu') return base + 1;
  if (resultadoRecente === 'dificil' || resultadoRecente === 'nao_conseguiu' || resultadoRecente === 'não conseguiu') return Math.max(1, base - 1);
  return base;
}

function escolherComboInedito(opcoes, combosUsados, seed) {
  const combos = produtoCartesiano(opcoes);
  const ineditos = combos.filter((combo) => !combosUsados.has(comboHash(combo)));
  const pool = ineditos.length ? ineditos : combos;
  return seededPick(seededShuffle(pool, seed), seed) || {};
}

function renderTexto(texto = '', combo = {}, aluno = {}) {
  return String(texto).replaceAll('{{contexto}}', combo.contexto || '').replaceAll('{{material}}', combo.material || '').replaceAll('{{apoio}}', combo.apoio || '').replaceAll('{{aluno}}', aluno.nome || aluno.name || 'aluno');
}

function instanciarRoteiro(roteiro = [], combo = {}, aluno = {}) {
  return roteiro.map((item, index) => ({ ...item, passo: item.passo || index + 1, acao: renderTexto(item.acao || item, combo, aluno), apoio: renderTexto(item.apoio || combo.apoio || '', combo, aluno) }));
}

function historicoDaAncora(historicoInstancias = [], ancoraId) {
  return (historicoInstancias || []).filter((item) => item.baseadoEm === ancoraId || item.atividadeId === ancoraId || item.activityId === ancoraId || item.atividade_id === ancoraId);
}

export function gerarInstanciaAtividade(ancora = {}, ctx = {}) {
  const aluno = ctx.aluno || {};
  const perfil = escolherPerfilPrincipal(aluno.necessidades || aluno.necessidadesEducacionais || aluno.deficiencias || aluno.necessidade);
  const baseNivel = Number(ancora.nivel_dificuldade || ancora.nivel || 1);
  const nivel = resultadoParaNivel(baseNivel, ctx.resultadoRecente);
  const seed = ctx.semente || `${aluno.id || aluno.alunoId || 'aluno'}:${ancora.id}:${new Date().toISOString().slice(0, 10)}`;
  const historico = historicoDaAncora(ctx.historicoInstancias || [], ancora.id);
  const usados = new Set(historico.map((item) => item.comboKey).filter(Boolean));
  const variacoes = ancora.variacoes || {};
  const opcoes = { material: filtrarPorPerfil(variacoes.material || ancora.materiais || ['material padrão'], perfil), contexto: priorizarInteresses(variacoes.contexto || [ancora.contexto || 'rotina escolar'], aluno.interesses || [], seed), apoio: apoioPorNivel(variacoes.apoio || ['visual', 'verbal', 'independente'], nivel), sensorial: filtrarPorPerfil(variacoes.sensorial || ['visual'], perfil), agrupamento: variacoes.agrupamento || [ancora.agrupamento || 'individual'] };
  const combo = escolherComboInedito(opcoes, usados, seed);
  const comboKey = comboHash(combo);
  const roteiro = instanciarRoteiro(ancora.roteiro || (ancora.passo_a_passo || []).map((acao, index) => ({ passo: index + 1, acao })), combo, aluno);
  return { instanciaId: `${ancora.id}::${comboKey}`, comboKey, baseadoEm: ancora.id, titulo: `${ancora.titulo}${combo.contexto && !String(ancora.titulo || '').toLowerCase().includes(String(combo.contexto).toLowerCase()) ? ` — ${combo.contexto}` : ''}`, habilidades_alvo: ancora.habilidades_alvo || ancora.habilidades || [], nivel, material: combo.material, contexto: combo.contexto, apoio: combo.apoio, sensorial: combo.sensorial, agrupamento: combo.agrupamento, roteiro, adaptacoes: ancora.adaptacoes_por_perfil?.[perfil] || ancora.adaptacoes?.orientacao || null, registro: ancora.registro || { como: ancora.como_medir_progresso || '' }, variacaoDe: 'gerador_v18' };
}

export function trocarVariacaoAtividade(ancora, ctx = {}) {
  const extraSeed = `${ctx.semente || ''}:troca:${Date.now()}`;
  return gerarInstanciaAtividade(ancora, { ...ctx, semente: extraSeed });
}
